import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ArrowRight, Award, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/reviews`);
        setReviews(response.data);
      } catch (_) {
        // Reviews fetch failed
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="reviews-page">

      {/* ── Orange Hero ──────────────────────────── */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="reviews-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F56F05] via-[#F56F05] to-[#C2410C]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Award className="h-4 w-4" /> 5-Star Rated Service
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white font-heading mb-5 tracking-tight drop-shadow-lg">
              What Our Clients Say
            </h1>
            <p className="text-base lg:text-lg text-white/90 max-w-2xl mx-auto mb-6 leading-relaxed">
              Don't just take our word for it — hear from homeowners who've experienced the Gleam Guru difference.
            </p>
            <div className="flex items-center justify-center gap-1.5">
              {[1, 2, 3, 4, 5].map(i => <Star key={i} className="h-8 w-8 fill-white text-white" />)}
              <span className="text-white font-bold text-xl ml-2">5.0</span>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" className="w-full"><path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/></svg>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────── */}
      <section className="py-12 bg-white border-b border-zinc-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: '500+', label: 'Happy Clients' },
            { value: '5.0', label: 'Average Rating' },
            { value: '98%', label: 'Would Recommend' },
            { value: '1000+', label: 'Cleans Completed' },
          ].map((s, i) => (
            <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.08 }} className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#F56F05] font-heading mb-1">{s.value}</p>
              <p className="text-sm text-zinc-500 font-medium">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Reviews Grid ─────────────────────────── */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-sm font-semibold text-[#F56F05] uppercase tracking-widest mb-3">Testimonials</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 font-heading">Real Stories From Real Clients</h2>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F56F05] mx-auto" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {reviews.map((review, i) => (
                <motion.div key={review.id || i} {...fadeUp} transition={{ delay: i * 0.06 }}
                  className={`rounded-2xl p-6 border-2 transition-all hover:shadow-lg ${
                    i % 2 === 0
                      ? 'bg-white border-zinc-100 hover:border-[#F56F05]/30'
                      : 'bg-gradient-to-br from-[#FFF7ED] to-white border-[#F56F05]/10 hover:border-[#F56F05]/30'
                  }`}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F56F05] to-[#C2410C] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#F56F05]/20">
                      <Quote className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="flex gap-0.5 mb-0.5">
                        {[...Array(review.rating || 5)].map((_, j) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Star key={j} className="h-4 w-4 fill-[#F56F05] text-[#F56F05]" />
                        ))}
                      </div>
                      <p className="text-xs text-zinc-400">{review.service}</p>
                    </div>
                  </div>
                  <p className="text-zinc-700 leading-relaxed mb-4">"{review.text}"</p>
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
                    <div>
                      <p className="font-bold text-zinc-900 text-sm">{review.name}</p>
                      <p className="text-xs text-zinc-400">{review.date}</p>
                    </div>
                    <div className="w-9 h-9 rounded-full bg-[#F56F05]/10 flex items-center justify-center">
                      <span className="text-[#F56F05] font-bold text-sm">{review.name?.charAt(0)}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────── */}
      <section className="relative py-20 md:py-28 bg-zinc-950 border-t border-white/5 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, #F56F05 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#F56F05] opacity-[0.1] blur-[140px] pointer-events-none"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <img src="https://customer-assets.emergentagent.com/job_09ef4963-c96f-46c6-a08d-052afb978ee9/artifacts/j66rn420_Gleam%20Guru%20Cleaning%20%5BLogo%20Design%5D%20No%20Gleam.png" alt="Gleam Guru" className="h-14 w-auto mx-auto" />
            <h2 className="text-3xl md:text-4xl font-extrabold text-white font-heading tracking-tight">
              Gleam With The <span className="bg-gradient-to-br from-[#FFB85C] via-[#F56F05] to-[#C2410C] bg-clip-text text-transparent">Masters Of Clean</span>
            </h2>
            <p className="text-zinc-400 text-sm max-w-lg mx-auto">
              Get in touch today and experience what premium cleaning feels like. No commitments, no pressure — just a conversation.
            </p>
            <div className="flex justify-center">
              <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.98 }}>
                <Button
                  asChild
                  className="relative overflow-hidden bg-[#F56F05] hover:bg-[#C2410C] text-white px-10 py-7 text-base rounded-full font-bold shadow-[0_20px_50px_-12px_rgba(245,111,5,0.55)] hover:shadow-[0_25px_60px_-12px_rgba(245,111,5,0.7)] transition-all group/cta"
                >
                  <Link to="/?service=#quote-form">
                    <span className="relative z-10 flex items-center">
                      Let&apos;s Get Your Space Gleaming <ArrowRight className="ml-2 w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover/cta:translate-x-full transition-transform duration-700 pointer-events-none" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
